import { FormHelperText, Typography } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/elements/Input";
import { DAILY_HOURS_INPUT_REGEX } from "@/features/dashboard/constants/dailyHoursInput";
import { DEFAULT_DAILY_STUDY_HOURS } from "@/features/dashboard/constants/studyTime";
import { background, text } from "@/theme/colors";

export type TDailyStudyHoursInputProps = {
  value: string;
  onChange: (val: string) => void;
  onBlur?: () => void;
  errorMessage?: string | null;
  /** Character-level filter. Defaults to onboarding's decimal-allowing regex. */
  inputPattern?: RegExp;
};

/**
 * Numeric input for daily study hours (unit: 時間).
 * Shared by onboarding (StudySettingsContent) and Settings page — validation
 * and touched-state live in the parent so it can drive save/error behavior.
 */
export const DailyStudyHoursInput: FC<TDailyStudyHoursInputProps> = ({
  value,
  onChange,
  onBlur,
  errorMessage,
  inputPattern = DAILY_HOURS_INPUT_REGEX,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Input
        nonControl
        size="lg"
        clearable={false}
        placeholder={t("studySettings.studyTime.placeholder", { defaultHours: DEFAULT_DAILY_STUDY_HOURS })}
        value={value}
        type="text"
        inputMode="decimal"
        error={!!errorMessage}
        onBlur={onBlur}
        onChange={(e) => {
          const next = e.target.value;
          // Reject keystrokes that exceed 3 digits / 2 decimals (half- or full-width).
          // Empty is always allowed so the field can be cleared. The controlled `value`
          // prop makes React restore the prior value when we skip the update.
          if (next === "" || inputPattern.test(next)) onChange(next);
        }}
        transformToHalfWidth
        endAdornment={
          <Typography variant="body14" color={text.primary} sx={{ ml: 1 }}>
            {t("studySettings.studyTime.unit")}
          </Typography>
        }
        sx={{
          bgcolor: background.white,
          maxWidth: "100%",
        }}
      />
      {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
    </>
  );
};
